import { effect, untracked, type Injector, type Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { TaskInterfacePanelComponent } from '@mtm/task-ui-angular';
import { MtmContextService } from '@mtm/context-ui-angular';
import { TASK_PANEL_CONTEXT_KEY } from '../../services/view-context-keys';

/**
 * Opens the task a newly arrived `?ctx=` names in a task panel that is already mounted.
 *
 * The task library restores `tid` once per panel instance. A navigation that keeps the
 * panel alive (same project), or one whose context is still loading when the panel
 * becomes ready, hands it a `tid` after that restore has run — so the panel shows the
 * list and later mirrors `tid` away. A panel's first context is left to the library.
 */
export function followContextTask(
  panel: Signal<TaskInterfacePanelComponent | undefined>,
  isTaskPanelOn: Signal<boolean>,
  isPluginReady: () => boolean,
  injector: Injector
): void {
  const viewContext: MtmContextService = injector.get(MtmContextService);
  let seenPanel: TaskInterfacePanelComponent | undefined;
  let seenCtx: string | null = null;

  effect(
    () => {
      const current: TaskInterfacePanelComponent | undefined = panel();
      const ctxUuid: string | null = viewContext.ctxUuid();
      if (!current) return;
      if (current !== seenPanel) {
        seenPanel = current;
        seenCtx = ctxUuid;
        return;
      }
      if (ctxUuid === seenCtx || !isTaskPanelOn() || current.store.isLoading() || !isPluginReady()) return;
      seenCtx = ctxUuid;
      untracked(() => {
        const taskUuid: string | null = viewContext.readValue(TASK_PANEL_CONTEXT_KEY.taskId);
        if (!taskUuid || taskUuid === current.editingTaskId()) return;
        const parentUuid: string | null = viewContext.readValue(TASK_PANEL_CONTEXT_KEY.parentTaskId);
        const boardUuid: string | null = viewContext.readValue(TASK_PANEL_CONTEXT_KEY.boardId);
        (parentUuid
          ? current.openSubtaskByUuid(parentUuid, taskUuid, boardUuid)
          : current.openTaskByUuid(taskUuid, boardUuid)
        )
          .pipe(takeUntilDestroyed(current.destroyRef))
          .subscribe();
      });
    },
    { injector }
  );
}
